import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { Input, Button, Toggle, Loading } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import { findProductSettings, updateProductSettings } from '../actions';

const ProductSettings = memo(
  ({
    productSettingsState: { productSettingsData },
    showAlert,
    findProductSettings,
    updateProductSettings,
  }) => {
    const originalDataRef = useRef({});
    const [productSettingsForm, setProductSettingsForm] = useState({
      'is-non-stock-type-billing-enabled': false,
      'default-low-stock-threshold': '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      findProductSettings();
    }, []);

    useEffect(() => {
      if (
        Array.isArray(productSettingsData) &&
        productSettingsData.length > 0
      ) {
        const nextForm = {
          'is-non-stock-type-billing-enabled': '',
          'default-low-stock-threshold': '',
        };
        for (const item of productSettingsData) {
          const paramKey = item?.param;
          const paramValue = item?.value;
          if (
            paramKey &&
            Object.prototype.hasOwnProperty.call(nextForm, paramKey)
          ) {
            nextForm[paramKey] = paramValue ?? '';
          }
        }
        originalDataRef.current = { ...nextForm };
        setProductSettingsForm(nextForm);
      }
    }, [productSettingsData]);

    const handleToggleChange = (e) => {
      const { name, checked } = e.target;
      setProductSettingsForm((prev) => ({
        ...prev,
        [name]: checked ? 'true' : 'false',
      }));
    };

    const handleChangeInputValues = (e) => {
      const { id, value } = e.target;
      setProductSettingsForm((prev) => ({
        ...prev,
        [id]: value,
      }));
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      const changedFields = {};
      Object.keys(productSettingsForm).forEach((key) => {
        const currentVal = productSettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }

      const productSettings = {
        productSettingsData: changedFields,
      };
      const result = await updateProductSettings(productSettings);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Product settings updated successfully!',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to update product settings!',
        });
      }
      setIsLoading(false);
      findProductSettings();
    };

    const onClickCancel = () => {
      navigate('/');
    };

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Product Settings</h1>
            <p className={styles.breadcrumb}>Settings / Product</p>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>General Product Settings</h2>
                  <p className={styles.topicCaption}>
                    Here you can change the general product settings
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={productSettingsForm['is-non-stock-type-billing-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-non-stock-type-billing-enabled"
                    label="Service Type Enable"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Input
                    id="default-low-stock-threshold"
                    label="Default Low Stock Threshold"
                    type="number"
                    value={productSettingsForm['default-low-stock-threshold']}
                    onInput={handleChangeInputValues}
                    placeholder="Default Low Stock Threshold"
                  />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClickCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  productSettingsState: state.productSettingsState,
});

export default connect(mapStateToProps, {
  showAlert,
  updateProductSettings,
  findProductSettings,
})(ProductSettings);
